import classNames from 'classnames'
import Select, {
  type GroupBase,
  type ControlProps,
  type DropdownIndicatorProps,
  components,
} from 'react-select'

import css from './SelectInput.styles.scss'

import { SelectInputProps, SelectInputCustomProps } from './SelectInput.types'
import { useState } from 'react'

function Control<O, IM extends boolean, G extends GroupBase<O>>({
  children,
  ...props
}: ControlProps<O, IM, G>) {
  // @ts-ignore
  const { LeftIcon } = props.selectProps

  const { isDisabled, isFocused, menuIsOpen } = props
  return (
    <components.Control {...props}>
      {LeftIcon && (
        <span className={classNames(css.input__icon, css.input__left_icon)}>
          <LeftIcon {...{ isDisabled, isFocused, menuIsOpen }} />
        </span>
      )}
      {children}
    </components.Control>
  )
}

function DropdownIndicator<O, IM extends boolean, G extends GroupBase<O>>(
  props: DropdownIndicatorProps<O, IM, G>
) {
  // @ts-ignore
  const { RightIcon } = props.selectProps

  const { isDisabled, isFocused } = props
  return (
    <components.DropdownIndicator {...props}>
      {RightIcon ? (
        <span className={classNames(css.input__icon, css.input__right_icon)}>
          <RightIcon {...{ isDisabled, isFocused }} />
        </span>
      ) : (
        <></>
      )}
    </components.DropdownIndicator>
  )
}

const selectSizeClassname = (size: SelectInputCustomProps['size']) => {
  switch (size) {
    case 'sm':
      return css.input__small
    case 'md':
      return css.input__medium
    case 'lg':
      return css.input__large
  }
}

const selectTypeClassname = (type: SelectInputCustomProps['type']) => {
  switch (type) {
    case 'primary':
      return css.input__primary
  }
}

const SelectInput = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  size,
  label,
  status,
  LeftIcon,
  RightIcon,
  selectClassName,
  type = 'primary',
  onFocus = () => {},
  onBlur = () => {},
  ...props
}: SelectInputProps<Option, IsMulti, Group>) => {
  const [isFocused, setIsFocused] = useState<boolean>(false)

  const SelectInputClasses = classNames(
    css.SelectInput,
    selectSizeClassname(size),
    selectTypeClassname(type),
    {
      [css.input__error]: status?.type === 'error',
      [css.input__focused]: isFocused,
      [css.input__disabled]: props.isDisabled,
    },
    selectClassName
  )

  // TODO: Once we are on React 18 we can use the useId() hook to generate a unique ID for the tooltip
  // Until then this little trick will convert a random number to a base 36 string
  const [uuid1] = useState<string>(Math.random().toString(36).substring(2))
  const labelMessageId = uuid1
  const [uuid2] = useState<string>(Math.random().toString(36).substring(2))
  const errorMessageId = uuid2

  const shouldShowErrorMessage = status?.type === 'error' && status.message

  return (
    <div className={SelectInputClasses} data-testid="select-component">
      {label && (
        <div id={labelMessageId} className={css.input__label}>
          {label}
        </div>
      )}
      <Select
        // @ts-ignore
        LeftIcon={LeftIcon}
        RightIcon={RightIcon}
        components={{
          IndicatorSeparator: null,
          Control: Control<Option, IsMulti, Group>,
          DropdownIndicator: DropdownIndicator<Option, IsMulti, Group>,
        }}
        classNames={{
          control: () => css.input__control,
          valueContainer: () => css.input__value_container,
          placeholder: () => css.input__placeholder,
          singleValue: () => css.input__value,
          dropdownIndicator: () => css.input__dropdown_indicator,
          option: (state) =>
            classNames(css.input__option, {
              [css.input__option_focused]: state.isFocused,
            }),
          menu: () => css.input__menu,
        }}
        aria-labelledby={labelMessageId}
        aria-errormessage={shouldShowErrorMessage ? errorMessageId : undefined}
        aria-invalid={status?.type === 'error'}
        onFocus={(e) => {
          setIsFocused(true)
          onFocus(e)
        }}
        onBlur={(e) => {
          setIsFocused(false)
          onBlur(e)
        }}
        {...props}
      />
      {shouldShowErrorMessage && (
        <div id={errorMessageId} className={css.input__error_message}>
          {status.message}
        </div>
      )}
    </div>
  )
}

export default SelectInput
